import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Formulario from '../../components/ui/Formulario';

import { usePreventDoubleClick } from '../hooks/usePreventDoubleClick';
import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';

import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';
import styles from './CadastroUnidadeConsumidora.module.css';

function CadastroUnidadeConsumidora() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { get, post, put } = useHttpClient();
    const { exibirErro } = useNotificacoes();

    const [formData, setFormData] = useState({
        idUsina: '',
        idUsuario: '',
        codCliente: '',
        nomeUnidade: '',
        distribuidora: '',
        localizacaoUnidade: '',
        stAplicarDescontoTusd: 0
    });
    const [criarClienteProvisorio, setCriarClienteProvisorio] = useState(false);
    const [nomeClienteProvisorio, setNomeClienteProvisorio] = useState('');

    const [dataLoaded, setDataLoaded] = useState(false);
    const ranOnce = useRef(false);

    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const idEmpresa = userData.empresa?.id || 0;

    const [usuarios, setUsuarios] = useState([]);
    const [usinas, setUsinas] = useState([]);
    const [distribuidoras, setDistribuidoras] = useState([]);

    const fetchUsinas = async () => {
        const filtrosUsinas = new URLSearchParams({ idEmpresa }).toString();
        const r = await get(`/hello-sol/usinas/buscar?${filtrosUsinas}`);
        const lista = Array.isArray(r) ? r : (r?.content ?? []);
        setUsinas(lista);
        return lista;
    };

    const fetchDistribuidoras = async () => {
        const r = await get(`/hello-sol/unidades-consumidoras/distribuidoras`);
        const lista = Array.isArray(r) ? r : (r?.content ?? []);
        setDistribuidoras(lista);
        return lista;
    };

    const fetchClientesDaUsina = async (idUsina) => {
        if (!idUsina) {
            setUsuarios([]);
            return [];
        }
        const r = await get(
            `/hello-sol/usuarios/buscarUsuarioUsina?` +
            new URLSearchParams({ idUsina, idTipoUsuario: 3 }).toString()
        );
        const lista = Array.isArray(r) ? r : (r?.content ?? []);
        setUsuarios(lista);
        return lista;
    };

    const detectUsinaByCliente = async (clienteId, listaUsinas) => {
        for (const u of listaUsinas) {
            try {
                const r = await get(
                    `/hello-sol/usuarios/buscarUsuarioUsina?` +
                    new URLSearchParams({ idUsina: u.id, idTipoUsuario: 3 }).toString()
                );
                const arr = Array.isArray(r) ? r : (r?.content ?? []);
                if (arr.some((c) => String(c.id) === String(clienteId))) {
                    return String(u.id);
                }
            } catch {
                // segue para a próxima usina
            }
        }
        return '';
    };

    useEffect(() => {
        if (!idEmpresa) return;
        if (ranOnce.current) return;
        ranOnce.current = true;

        (async () => {
            try {
                const todasUsinas = await fetchUsinas();
                await fetchDistribuidoras();

                if (id) {
                    const d = await get(`/hello-sol/unidades-consumidoras/${id}`);

                    const clienteId = String(d?.cliente?.id ?? d?.idUsuario ?? d?.idCliente ?? '');
                    let usinaId = String(d?.usina?.id ?? d?.idUsina ?? d?.usinaId ?? '');

                    if (!usinaId && clienteId) {
                        usinaId = await detectUsinaByCliente(clienteId, todasUsinas);
                    }

                    if (usinaId) {
                        await fetchClientesDaUsina(usinaId);
                    }

                    if (clienteId) {
                        setUsuarios((curr) => {
                            const has = curr.some((u) => String(u.id) === clienteId);
                            if (has) return curr;
                            const nomeCliente = d?.cliente?.nome ?? 'Cliente selecionado';
                            return [{ id: Number(clienteId), nome: nomeCliente }, ...curr];
                        });
                    }

                    setFormData({
                        idUsina: usinaId,
                        idUsuario: clienteId,
                        codCliente: d?.codCliente ?? '',
                        nomeUnidade: d?.nomeUnidade ?? '',
                        localizacaoUnidade: d?.localizacaoUnidade ?? '',
                        distribuidora: d?.distribuidora ?? '',
                        stAplicarDescontoTusd: d?.stAplicarDescontoTusd ?? 0
                    });
                }

                setDataLoaded(true);
            } catch (e) {
                console.error('Falha ao carregar unidade consumidora:', e);
                exibirErro(
                    e?.response?.data?.message ||
                    e?.message ||
                    'Erro ao carregar unidade consumidora.'
                );
                setDataLoaded(true);
            }
        })();
    }, [idEmpresa, id, get, exibirErro]);

    useEffect(() => {
        if (!formData.idUsina || id) return;
        (async () => {
            try {
                await fetchClientesDaUsina(formData.idUsina);
            } catch {
                exibirErro('Erro ao buscar clientes da usina.');
            }
        })();
    }, [formData.idUsina, id, get, exibirErro]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => {
            const novo = { ...prev, [name]: value };
            if (name === 'idUsina' && !id) {
                novo.idUsuario = '';
                novo.codCliente = '';
                novo.nomeUnidade = '';
                novo.localizacaoUnidade = '';
                novo.distribuidora = '';
                setCriarClienteProvisorio(false);
                setNomeClienteProvisorio('');
            }
            return novo;
        });
    };

    const handleToggleClienteProvisorio = (event) => {
        const checked = event.target.checked;
        setCriarClienteProvisorio(checked);
        if (checked) {
            setFormData((prev) => ({ ...prev, idUsuario: '' }));
        } else {
            setNomeClienteProvisorio('');
        }
    };

    const salvarUnidadeConsumidora = async () => {
        try {
            if (!id && !formData.idUsina) {
                exibirErro('Selecione a usina.');
                return;
            }

            if (!id && !criarClienteProvisorio && !formData.idUsuario) {
                exibirErro('Selecione um cliente ou marque a criação de cliente provisório.');
                return;
            }

            if (!id && criarClienteProvisorio && !nomeClienteProvisorio.trim()) {
                exibirErro('Informe o nome do cliente provisório.');
                return;
            }

            const payload = {
                idCliente: criarClienteProvisorio ? null : Number(formData.idUsuario),
                codCliente: formData.codCliente,
                nomeUnidade: formData.nomeUnidade,
                localizacaoUnidade: formData.localizacaoUnidade,
                distribuidora: formData.distribuidora,
                idUsina: Number(formData.idUsina || 0),
                stAplicarDescontoTusd: formData.stAplicarDescontoTusd,
                criarClienteProvisorio,
                nomeClienteProvisorio: criarClienteProvisorio ? nomeClienteProvisorio.trim() : null
            };

            if (id) await put(`/hello-sol/unidades-consumidoras/${id}`, payload);
            else await post('/hello-sol/unidades-consumidoras', payload);

            navigate('/home/listar-unidade-consumidora');
        } catch (err) {
            exibirErro(err?.message || 'Erro ao salvar a unidade consumidora.');
        }
    };

    const [safeSubmit, isSubmitting] = usePreventDoubleClick(salvarUnidadeConsumidora);

    return (
        <div className={styles_global.container}>
            <h2>{id ? 'Alterar Unidade Consumidora' : 'Cadastrar Unidade Consumidora'}</h2>

            <Formulario onSubmit={safeSubmit}>
                <div className={styles.linha2}>
                    <div className={styles_content.formField}>
                        <label htmlFor="idUsina">Usina*</label>
                        <select
                            id="idUsina"
                            className={styles.campoUsina}
                            name="idUsina"
                            value={formData.idUsina}
                            onChange={handleChange}
                            required
                            disabled={!!id}
                        >
                            <option value="">Selecionar Usina</option>
                            {usinas.map((u) => (
                                <option key={u.id} value={String(u.id)}>
                                    {u.nome}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="idUsuario">Cliente*</label>
                        <select
                            id="idUsuario"
                            name="idUsuario"
                            className={styles.campoCliente}
                            value={formData.idUsuario}
                            onChange={handleChange}
                            required={!criarClienteProvisorio}
                            disabled={!!id || criarClienteProvisorio}
                        >
                            <option value="">Selecionar Cliente</option>
                            {usuarios.map((u) => (
                                <option key={u.id} value={String(u.id)}>
                                    {u.nome}{u.cadastroPendente ? ' (pendente)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {!id && (
                    <>
                        <div className={styles.linha2}>
                            <div className={styles_content.formField}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <input
                                        type="checkbox"
                                        checked={criarClienteProvisorio}
                                        onChange={handleToggleClienteProvisorio}
                                    />
                                    Criar cliente provisório automaticamente
                                </label>
                            </div>
                        </div>

                        {criarClienteProvisorio && (
                            <div className={styles.linha2}>
                                <div className={styles_content.formField}>
                                    <label htmlFor="nomeClienteProvisorio">Nome do cliente provisório*</label>
                                    <input
                                        id="nomeClienteProvisorio"
                                        type="text"
                                        value={nomeClienteProvisorio}
                                        onChange={(e) => setNomeClienteProvisorio(e.target.value)}
                                        className={styles.campoUnidade}
                                        maxLength={100}
                                        placeholder="Ex: Track - Boulevard Shopping"
                                        required={criarClienteProvisorio}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}

                <div className={styles.linha2}>
                    <div className={styles_content.formField}>
                        <label htmlFor="distribuidora">Distribuidora</label>
                        <select
                            id="distribuidora"
                            name="distribuidora"
                            value={formData.distribuidora}
                            onChange={handleChange}
                        >
                            <option value="">Selecione a distribuidora</option>
                            {distribuidoras.map((d) => (
                                <option key={d.name} value={d.name}>
                                    {d.display} ({d.uf})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles_content.formField}>
                        <label htmlFor="codCliente">Cód. Unidade Consumidora</label>
                        <input
                            type="text"
                            className={styles.campoCodigo}
                            name="codCliente"
                            value={formData.codCliente}
                            onChange={handleChange}
                            id="codCliente"
                            maxLength={20}
                        />
                    </div>
                </div>
                <div className={styles.linha2}>
                    <div className={styles_content.formField}>
                        <label htmlFor="nomeUnidade">Nome Unidade</label>
                        <input
                            type="text"
                            className={styles.campoUnidade}
                            name="nomeUnidade"
                            value={formData.nomeUnidade}
                            onChange={handleChange}
                            id="nomeUnidade"
                            maxLength={150}
                        />
                    </div>

                    <div className={styles_content.formField}>
                        <label htmlFor="localizacaoUnidade">Localização da Unidade</label>
                        <input
                            id="localizacaoUnidade"
                            name="localizacaoUnidade"
                            type="text"
                            value={formData.localizacaoUnidade}
                            onChange={handleChange}
                            className={styles.campoLocalizacao}
                            placeholder="Bloco A"
                            required
                        />
                    </div>
                </div>

                <div className={styles.linha2}>
                    <div className={styles_content.formField}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', whiteSpace: 'nowrap' }}>
                            <input
                                type="checkbox"
                                checked={formData.stAplicarDescontoTusd === 1}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        stAplicarDescontoTusd: e.target.checked ? 1 : 0
                                    }))
                                }
                            />
                            Aplicar Desconto TUSD
                        </label>
                    </div>
                </div>

                <div className={styles_content.buttonGroup}>
                    <button type="submit" disabled={isSubmitting || !dataLoaded}>
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                    </button>
                </div>
            </Formulario>
        </div>
    );
}

export default CadastroUnidadeConsumidora;
